// Mobile-web "Add to Chat" bottom sheet — mirrors the native app's sheet:
// grab handle, titled header, Photos/Files tiles, and a Reasoning row that
// drills into the options list in place.
import { useEffect, useState } from "react";
import { Box, ButtonBase, Drawer, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ShortTextRoundedIcon from "@mui/icons-material/ShortTextRounded";
import { isDarkMode } from "../../utils/constants";
import { REASONING_OPTIONS, reasoningLabelFor } from "./reasoningModes";
import {
  RESPONSE_LENGTH_OPTIONS,
  responseLengthLabelFor,
} from "./responsePreferences";

const ComposerAddSheet = ({
  open,
  onClose,
  showReasoning,
  reasoningMode,
  onSelectReasoning,
  showResponseLength,
  responseLength,
  onSelectResponseLength,
  onPickPhotos,
  onPickFiles,
}) => {
  const [view, setView] = useState("main");

  useEffect(() => {
    if (open) setView("main");
  }, [open]);

  const textColor = isDarkMode ? "#f5f5f5" : "#121212";
  const mutedColor = isDarkMode
    ? "rgba(255,255,255,0.55)"
    : "rgba(18,18,18,0.55)";
  const surfaceColor = isDarkMode ? "#252525" : "#ECEEF3";
  const maxLabelColor = isDarkMode ? "#FCD34D" : "#92400E";

  const tile = (icon, label, onClick) => (
    <ButtonBase
      onClick={() => {
        onClose();
        onClick();
      }}
      sx={{
        flex: 1,
        width: "100%",
        height: 96,
        borderRadius: "20px",
        backgroundColor: surfaceColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        cursor: "pointer",
        color: textColor,
        "&:active": { transform: "scale(0.97)" },
        transition: "transform 160ms ease",
      }}
    >
      {icon}
      <Box sx={{ fontSize: "0.92rem", fontWeight: 500 }}>{label}</Box>
    </ButtonBase>
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: "28px",
            borderTopRightRadius: "28px",
            backgroundColor: isDarkMode ? "#171717" : "#ffffff",
            backgroundImage: "none",
            px: 2,
            pt: 1,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          },
        },
      }}
    >
      <Box
        sx={{
          alignSelf: "center",
          width: 40,
          height: 5,
          borderRadius: "3px",
          backgroundColor: isDarkMode
            ? "rgba(255,255,255,0.2)"
            : "rgba(0,0,0,0.16)",
          mb: 1.5,
        }}
      />

      <Box
        sx={{
          position: "relative",
          height: 44,
          display: "flex",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: "1.02rem",
            fontWeight: 700,
            color: textColor,
          }}
        >
          {view === "reasoning"
            ? "Reasoning"
            : view === "responseLength"
              ? "Response length"
              : "Add to Chat"}
        </Box>
        <IconButton
          onClick={view !== "main" ? () => setView("main") : onClose}
          aria-label={view !== "main" ? "Back" : "Close"}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: surfaceColor,
            color: textColor,
            "&:hover": { backgroundColor: surfaceColor },
          }}
        >
          {view !== "main" ? (
            <ChevronLeftIcon sx={{ fontSize: 24 }} />
          ) : (
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>

      {view !== "main" ? (
        <Box>
          {(view === "reasoning"
            ? REASONING_OPTIONS
            : RESPONSE_LENGTH_OPTIONS
          ).map((option) => {
            const isSelected =
              option.value ===
              (view === "reasoning" ? reasoningMode : responseLength);
            const isMax = option.value === "max";
            return (
              <ButtonBase
                key={option.value}
                data-chat-response-length-option={
                  view === "responseLength" ? option.value : undefined
                }
                onClick={() => {
                  onClose();
                  if (view === "reasoning") {
                    onSelectReasoning(option.value);
                  } else {
                    onSelectResponseLength(option.value);
                  }
                }}
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 1.75,
                  py: 1.5,
                  borderRadius: "16px",
                  mb: 0.25,
                  cursor: "pointer",
                  backgroundColor: isSelected ? surfaceColor : "transparent",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      color: isMax ? maxLabelColor : textColor,
                    }}
                  >
                    {option.label}
                    {isMax && (
                      <BoltRoundedIcon sx={{ fontSize: 15, color: "#F59E0B" }} />
                    )}
                  </Box>
                  <Box sx={{ fontSize: "0.76rem", color: mutedColor, mt: 0.25 }}>
                    {option.description}
                  </Box>
                </Box>
                {isSelected && (
                  <CheckRoundedIcon sx={{ fontSize: 20, color: textColor }} />
                )}
              </ButtonBase>
            );
          })}
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {tile(
              <ImageOutlinedIcon sx={{ fontSize: 26 }} />,
              "Photos",
              onPickPhotos,
            )}
            {tile(
              <UploadFileOutlinedIcon sx={{ fontSize: 26 }} />,
              "Files",
              onPickFiles,
            )}
          </Box>

          {showReasoning && (
            <ButtonBase
              onClick={() => setView("reasoning")}
              sx={{
                width: "100%",
                mt: 1.5,
                px: 2,
                py: 2,
                borderRadius: "20px",
                backgroundColor: surfaceColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  color: textColor,
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                <BoltRoundedIcon sx={{ fontSize: 20 }} />
                Reasoning
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  color: mutedColor,
                  fontSize: "0.88rem",
                }}
              >
                {reasoningLabelFor(reasoningMode)}
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </Box>
            </ButtonBase>
          )}
          {showResponseLength && (
            <ButtonBase
              data-chat-response-length-sheet-row
              onClick={() => setView("responseLength")}
              sx={{
                width: "100%",
                mt: 1.1,
                px: 2,
                py: 2,
                borderRadius: "20px",
                backgroundColor: surfaceColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  color: textColor,
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                <ShortTextRoundedIcon sx={{ fontSize: 20 }} />
                Response length
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  color: mutedColor,
                  fontSize: "0.88rem",
                }}
              >
                {responseLengthLabelFor(responseLength)}
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </Box>
            </ButtonBase>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ComposerAddSheet;
